import { useMemo } from "react";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import useOrgDirectory from "hooks/useOrgDirectory";
import CrmMemberAvatar from "../crm/org/CrmMemberAvatar";
import { normalizeUserEmail } from "utils/calendar/officeTasks";

type AssigneeOption = { userId: string; name: string; avatarUrl: string | null };

type CalendarTaskAssigneeSelectProps = {
  /** Lowercased email of whose calendar the task should be in. */
  value: string;
  onChange: (userId: string) => void;
  /** The signed-in user. */
  me: string;
  /** Whose calendar the task is in now — listed even if they have since left the office. */
  ownerId: string;
  disabled?: boolean;
};

/**
 * "באחריות" — whose calendar a task goes into: the user, or a colleague from their office
 * (`useOrgDirectory`, the server's `users ∪ admins`). The choice is only a request: the server
 * checks the two still share an office before it writes anything.
 */
export function CalendarTaskAssigneeSelect({
  value,
  onChange,
  me,
  ownerId,
  disabled
}: CalendarTaskAssigneeSelectProps): JSX.Element {
  const { t: translate } = useTranslation();
  const { members, getMember } = useOrgDirectory();

  const options = useMemo<AssigneeOption[]>(() => {
    const list: AssigneeOption[] = members.map((member) => ({
      userId: normalizeUserEmail(member.userId),
      name: member.displayName ?? member.userId,
      avatarUrl: member.avatarUrl
    }));
    for (const userId of [me, ownerId]) {
      if (userId && !list.some((option) => option.userId === userId)) {
        list.push({ userId, name: getMember(userId)?.displayName ?? userId, avatarUrl: null });
      }
    }
    // The user first: "אני" is the default and the most common choice.
    return [...list.filter((option) => option.userId === me), ...list.filter((option) => option.userId !== me)];
  }, [members, me, ownerId, getMember]);

  const labelOf = (userId: string): string => {
    if (userId === me) return translate("calendarPage.officeTasks.me");
    return options.find((option) => option.userId === userId)?.name ?? userId;
  };

  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel>{translate("calendarPage.officeTasks.assignee")}</InputLabel>
      <Select
        value={value}
        label={translate("calendarPage.officeTasks.assignee")}
        onChange={({ target }) => onChange(target.value as string)}
        renderValue={(selected) => labelOf(selected as string)}
      >
        {options.map((option) => (
          <MenuItem value={option.userId} key={option.userId}>
            <Stack direction="row" gap={1.5} alignItems="center">
              <CrmMemberAvatar name={option.name} avatarUrl={option.avatarUrl} size={24} />
              {labelOf(option.userId)}
            </Stack>
          </MenuItem>
        ))}
      </Select>
      {value !== me && (
        <FormHelperText>
          {translate("calendarPage.officeTasks.assigneeHelper", { name: labelOf(value) })}
        </FormHelperText>
      )}
    </FormControl>
  );
}

/** Whether the user has anyone to assign a task to: an office with at least one colleague. */
export function useHasOfficeColleagues(): boolean {
  const { members } = useOrgDirectory();
  return members.length > 1;
}
